import { useEffect, useMemo, useState } from "react";
import { Table, Button, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import ModalUsuario, { ESTRATEGIAS_DISPONIBLES } from "../modal/ModalUsuario";
import {
  crearUsuarioAPI,
  listarUsuariosAPI,
  actualizarUsuarioAPI,
  desactivarUsuarioAPI,
  aprobarUsuarioAPI,
  rechazarUsuarioAPI,
} from "../api/usuariosAPI";
import "../styles/admin.css";

const ITEMS_POR_PAGINA = 10;
const AZUL = "#1a3a5c";

// Roles disponibles para el filtro. Incluye auditor/certificador aunque el
// modal de creación ya no los ofrezca — puede haber cuentas viejas con esos
// roles y siguen debiendo aparecer en el filtro.
const ROLES_FILTRO = ["todos", "admin", "auditor", "certificador", "usuario"];

// Estado "visible" de una cuenta. `estado` viene del registro público
// (pendiente/rechazado/aprobado); las cuentas viejas no lo traen y se
// consideran aprobadas.
function estadoDe(u) {
  if (u.estado === "pendiente") return "pendiente";
  if (u.estado === "rechazado") return "rechazado";
  return u.activo ? "activo" : "inactivo";
}

const ESTADOS = {
  pendiente: { texto: "Pendiente", clase: "ambar", icono: "⏳" },
  rechazado: { texto: "Rechazado", clase: "rojo", icono: "✕" },
  activo: { texto: "Activo", clase: "verde", icono: "●" },
  inactivo: { texto: "Inactivo", clase: "gris", icono: "○" },
};

const PESTANAS = [
  { valor: "todos", texto: "Todos" },
  { valor: "pendiente", texto: "Pendientes" },
  { valor: "activo", texto: "Activos" },
  { valor: "inactivo", texto: "Inactivos" },
  { valor: "rechazado", texto: "Rechazados" },
];

const ETIQUETA_PROGRAMA = Object.fromEntries(ESTRATEGIAS_DISPONIBLES.map((e) => [e.value, e.label]));

// Quita tildes para que "Jose" encuentre a "José" y viceversa.
function normalizar(texto) {
  return (texto || "")
    .toString()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

function formatearFecha(f) {
  if (!f) return "";
  const d = new Date(f);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" });
}

function PillEstado({ u }) {
  const e = ESTADOS[estadoDe(u)];
  return <span className={`ps-pill ${e.clase}`}>{e.icono} {e.texto}</span>;
}

function PillRol({ rol }) {
  return <span className={`ps-pill ${rol === "admin" ? "azul" : rol === "auditor" || rol === "certificador" ? "ambar" : "gris"}`}>{rol}</span>;
}

function Programas({ u }) {
  if (u.rol === "admin") return <span className="text-muted">Todas (admin)</span>;
  const lista = Array.isArray(u.programas) ? u.programas : [];
  if (lista.includes("TODOS")) return <span>Todas</span>;
  if (!lista.length) return <span className="text-danger">Sin programas</span>;
  return (
    <span className="admin-programas">
      {lista.map((p) => <span key={p}>{ETIQUETA_PROGRAMA[p] || p}</span>)}
    </span>
  );
}

export default function AdminUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [busqueda, setBusqueda] = useState("");
  const [filtroRol, setFiltroRol] = useState("todos");
  const [pestana, setPestana] = useState("todos");

  const [modalAbierto, setModalAbierto] = useState(false);
  const [modoModal, setModoModal] = useState("crear"); // "crear" | "editar" | "aprobar"
  const [usuarioEditando, setUsuarioEditando] = useState(null);

  async function cargarUsuarios() {
    setCargando(true);
    try {
      const data = await listarUsuariosAPI();
      setUsuarios(data);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "No se pudo cargar la lista de usuarios",
        text: error?.response?.data?.error || "",
        confirmButtonColor: AZUL,
      });
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const conteos = useMemo(() => {
    const c = { todos: usuarios.length, pendiente: 0, activo: 0, inactivo: 0, rechazado: 0 };
    usuarios.forEach((u) => { c[estadoDe(u)] += 1; });
    return c;
  }, [usuarios]);

  const usuariosFiltrados = useMemo(() => {
    const texto = normalizar(busqueda);

    return usuarios
      .filter((u) => {
        const coincideTexto =
          !texto ||
          normalizar(u.nombre).includes(texto) ||
          normalizar(u.identificacion).includes(texto) ||
          normalizar(u.email).includes(texto);
        const coincideRol = filtroRol === "todos" || u.rol === filtroRol;
        const coincideEstado = pestana === "todos" || estadoDe(u) === pestana;
        return coincideTexto && coincideRol && coincideEstado;
      })
      // Las solicitudes pendientes siempre arriba — es lo que el admin tiene que atender.
      .sort((a, b) => (estadoDe(a) === "pendiente" ? 0 : 1) - (estadoDe(b) === "pendiente" ? 0 : 1));
  }, [usuarios, busqueda, filtroRol, pestana]);

  // Cualquier cambio en el filtro/búsqueda vuelve a la página 1.
  useEffect(() => {
    setPagina(1);
  }, [busqueda, filtroRol, pestana]);

  const totalPaginas = Math.max(1, Math.ceil(usuariosFiltrados.length / ITEMS_POR_PAGINA));

  const usuariosPagina = useMemo(() => {
    const inicio = (pagina - 1) * ITEMS_POR_PAGINA;
    return usuariosFiltrados.slice(inicio, inicio + ITEMS_POR_PAGINA);
  }, [usuariosFiltrados, pagina]);

  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [pagina, totalPaginas]);

  function abrirModal(modo, usuario = null) {
    setModoModal(modo);
    setUsuarioEditando(usuario);
    setModalAbierto(true);
  }

  function cerrarModal() {
    if (guardando) return; // evita cerrar a medio guardado
    setModalAbierto(false);
  }

  async function manejarGuardar(form) {
    setGuardando(true);
    try {
      if (modoModal === "crear") {
        await crearUsuarioAPI(form);
        Swal.fire({ icon: "success", title: "Usuario creado", confirmButtonColor: AZUL });
      } else if (modoModal === "aprobar") {
        await aprobarUsuarioAPI(usuarioEditando.id, { rol: form.rol, programas: form.programas });
        Swal.fire({
          icon: "success",
          title: "Cuenta aprobada",
          text: `${usuarioEditando.nombre} ya puede iniciar sesión.`,
          confirmButtonColor: AZUL,
        });
      } else {
        await actualizarUsuarioAPI(usuarioEditando.id, {
          nombre: form.nombre,
          email: form.email,
          numero_contrato: form.numero_contrato,
          rol: form.rol,
          programas: form.programas,
        });
        Swal.fire({ icon: "success", title: "Usuario actualizado", confirmButtonColor: AZUL });
      }
      setModalAbierto(false);
      cargarUsuarios();
    } catch (error) {
      const titulos = { crear: "No se pudo crear el usuario", aprobar: "No se pudo aprobar", editar: "No se pudo actualizar" };
      Swal.fire({
        icon: "error",
        title: titulos[modoModal],
        text: error?.response?.data?.error || "",
        confirmButtonColor: AZUL,
      });
    } finally {
      setGuardando(false);
    }
  }

  async function manejarRechazar(usuario) {
    const confirmacion = await Swal.fire({
      icon: "warning",
      title: `¿Rechazar la solicitud de ${usuario.nombre}?`,
      text: "No podrá iniciar sesión. Podrás aprobarla más adelante si fue un error.",
      showCancelButton: true,
      confirmButtonText: "Sí, rechazar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
    });
    if (!confirmacion.isConfirmed) return;

    try {
      await rechazarUsuarioAPI(usuario.id);
      cargarUsuarios();
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se pudo rechazar", text: error?.response?.data?.error || "", confirmButtonColor: AZUL });
    }
  }

  // "Eliminar" = baja lógica (activo=false), nunca se borra el registro —
  // igual que el resto del sistema (todo queda para auditoría).
  async function manejarEliminar(usuario) {
    const confirmacion = await Swal.fire({
      icon: "warning",
      title: `¿Desactivar a ${usuario.nombre}?`,
      text: "No podrá iniciar sesión hasta que se reactive. No se borra su historial.",
      showCancelButton: true,
      confirmButtonText: "Sí, desactivar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
    });
    if (!confirmacion.isConfirmed) return;

    try {
      await desactivarUsuarioAPI(usuario.id);
      cargarUsuarios();
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se pudo desactivar", text: error?.response?.data?.error || "", confirmButtonColor: AZUL });
    }
  }

  async function manejarReactivar(usuario) {
    try {
      await actualizarUsuarioAPI(usuario.id, { activo: true });
      cargarUsuarios();
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se pudo reactivar", text: error?.response?.data?.error || "", confirmButtonColor: AZUL });
    }
  }

  // Botones de acción según el estado de la cuenta (se usan en tabla y tarjetas).
  function Acciones({ u, enTarjeta }) {
    const estado = estadoDe(u);
    const clase = enTarjeta ? "flex-fill" : "";
    if (estado === "pendiente" || estado === "rechazado") {
      return (
        <>
          <Button size="sm" variant="success" className={clase} onClick={() => abrirModal("aprobar", u)}>✓ Aprobar</Button>
          {estado === "pendiente" && (
            <Button size="sm" variant="outline-danger" className={clase} onClick={() => manejarRechazar(u)}>✕ Rechazar</Button>
          )}
        </>
      );
    }
    return (
      <>
        <Button size="sm" variant="outline-primary" className={clase} onClick={() => abrirModal("editar", u)}>✏️ Editar</Button>
        {u.activo ? (
          <Button size="sm" variant="outline-danger" className={clase} onClick={() => manejarEliminar(u)}>Desactivar</Button>
        ) : (
          <Button size="sm" variant="outline-success" className={clase} onClick={() => manejarReactivar(u)}>↺ Reactivar</Button>
        )}
      </>
    );
  }

  const mensajeVacio = cargando
    ? "Cargando usuarios..."
    : usuarios.length === 0
      ? "Sin usuarios todavía"
      : pestana === "pendiente" && !busqueda && filtroRol === "todos"
        ? "🎉 No hay solicitudes pendientes"
        : "Ningún usuario coincide con la búsqueda o el filtro";

  return (
    <div className="admin-usuarios py-2">
      {/* ── Encabezado ── */}
      <div className="admin-hero">
        <div>
          <div className="admin-kicker">Administración</div>
          <h3>Usuarios y permisos</h3>
          <p>Aprueba las solicitudes de cuenta y define a qué estrategias accede cada persona.</p>
        </div>
        <Button className="btn-ps admin-nuevo" onClick={() => abrirModal("crear")}>
          + Nuevo usuario
        </Button>
      </div>

      {/* ── Aviso de pendientes ── */}
      {conteos.pendiente > 0 && pestana !== "pendiente" && (
        <button type="button" className="admin-aviso" onClick={() => setPestana("pendiente")}>
          <span className="admin-aviso-icono">⏳</span>
          <span>
            <strong>{conteos.pendiente} solicitud{conteos.pendiente === 1 ? "" : "es"} de cuenta</strong> esperando tu aprobación
          </span>
          <span className="admin-aviso-cta">Revisar ›</span>
        </button>
      )}

      {/* ── Pestañas de estado ── */}
      <div className="admin-tabs" role="tablist">
        {PESTANAS.map((p) => (
          <button
            key={p.valor}
            type="button"
            role="tab"
            aria-selected={pestana === p.valor}
            className={`admin-tab ${pestana === p.valor ? "activa" : ""} ${p.valor === "pendiente" && conteos.pendiente > 0 ? "alerta" : ""}`}
            onClick={() => setPestana(p.valor)}
          >
            {p.texto}
            <span className="admin-tab-n">{conteos[p.valor]}</span>
          </button>
        ))}
      </div>

      {/* ── Filtros ── */}
      <div className="admin-filtros">
        <Form.Control
          type="search"
          placeholder="🔎 Buscar por nombre, identificación o correo..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <Form.Select value={filtroRol} onChange={(e) => setFiltroRol(e.target.value)}>
          {ROLES_FILTRO.map((r) => (
            <option key={r} value={r}>{r === "todos" ? "Todos los roles" : r}</option>
          ))}
        </Form.Select>
      </div>

      {/* ── Tabla (tablet / escritorio) ── */}
      <div className="ps-surface overflow-hidden d-none d-md-block">
        <Table hover responsive className="mb-0 align-middle admin-tabla">
          <thead>
            <tr>
              <th className="ps-3">Usuario</th>
              <th>Identificación</th>
              <th>Rol</th>
              <th>Programas</th>
              <th>Estado</th>
              <th className="text-end pe-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {(cargando || usuariosFiltrados.length === 0) && (
              <tr><td colSpan={6} className="text-center text-muted py-5">{mensajeVacio}</td></tr>
            )}
            {!cargando && usuariosPagina.map((u) => (
              <tr key={u.id} className={estadoDe(u) === "pendiente" ? "fila-pendiente" : ""}>
                <td className="ps-3">
                  <div className="fw-semibold" style={{ color: AZUL }}>{u.nombre}</div>
                  <div className="text-muted" style={{ fontSize: "0.78rem" }}>{u.email}</div>
                </td>
                <td>
                  <span className="admin-mono">{u.identificacion}</span>
                  {u.numero_contrato && <div className="text-muted" style={{ fontSize: "0.72rem" }}>Contrato {u.numero_contrato}</div>}
                </td>
                <td><PillRol rol={u.rol} /></td>
                <td style={{ fontSize: "0.8rem", maxWidth: 240 }}><Programas u={u} /></td>
                <td>
                  <PillEstado u={u} />
                  {estadoDe(u) === "pendiente" && u.createdAt && (
                    <div className="text-muted" style={{ fontSize: "0.7rem", marginTop: 3 }}>Solicitó {formatearFecha(u.createdAt)}</div>
                  )}
                </td>
                <td className="text-end pe-3">
                  <div className="d-inline-flex gap-2 flex-nowrap">
                    <Acciones u={u} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* ── Tarjetas (celular) ── */}
      <div className="d-md-none">
        {(cargando || usuariosFiltrados.length === 0) && (
          <div className="ps-surface text-center text-muted py-5">{mensajeVacio}</div>
        )}
        {!cargando && usuariosPagina.map((u) => (
          <div key={u.id} className={`admin-tarjeta ps-surface ${estadoDe(u) === "pendiente" ? "pendiente" : ""}`}>
            <div className="d-flex justify-content-between align-items-start gap-2">
              <div style={{ minWidth: 0 }}>
                <div className="fw-bold text-truncate" style={{ color: AZUL }}>{u.nombre}</div>
                <div className="text-muted text-truncate" style={{ fontSize: "0.78rem" }}>{u.email}</div>
              </div>
              <PillEstado u={u} />
            </div>
            <dl className="admin-datos">
              <div><dt>Identificación</dt><dd className="admin-mono">{u.identificacion}</dd></div>
              <div><dt>Contrato</dt><dd>{u.numero_contrato || "—"}</dd></div>
              <div><dt>Rol</dt><dd><PillRol rol={u.rol} /></dd></div>
              <div className="ancho"><dt>Programas</dt><dd><Programas u={u} /></dd></div>
            </dl>
            <div className="d-flex gap-2">
              <Acciones u={u} enTarjeta />
            </div>
          </div>
        ))}
      </div>

      {/* ── Paginación ── */}
      {!cargando && usuariosFiltrados.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
          <span className="text-muted" style={{ fontSize: "0.82rem" }}>
            Página {pagina} de {totalPaginas} · {usuariosFiltrados.length} usuario{usuariosFiltrados.length === 1 ? "" : "s"}
          </span>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" disabled={pagina === 1} onClick={() => setPagina((p) => Math.max(1, p - 1))}>
              ‹ Anterior
            </Button>
            <Button size="sm" variant="outline-secondary" disabled={pagina === totalPaginas} onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}>
              Siguiente ›
            </Button>
          </div>
        </div>
      )}

      <ModalUsuario
        abierto={modalAbierto}
        modo={modoModal}
        usuarioInicial={usuarioEditando}
        guardando={guardando}
        onGuardar={manejarGuardar}
        onCerrar={cerrarModal}
      />
    </div>
  );
}
